/** These are retention limits, not instructions to abbreviate an argument.
 * Transport completion and retention completeness are independent facts. */
export const RESEARCH_TEXT_CHARS = 64_000
export const RESEARCH_ITEM_CHARS = 8_000
export const SOURCE_QUOTE_CHARS = 4_000

export interface TextTruncation {
  field: string
  boundary: 'admission' | 'persistence'
  unit: 'characters' | 'items'
  original: number
  retained: number
}

export function parseTextTruncations(value: unknown): TextTruncation[] | undefined {
  if (!Array.isArray(value) || !value.length || value.length > 128) return undefined
  if (
    !value.every((row: unknown) => {
      if (!row || typeof row !== 'object') return false
      const r = row as TextTruncation
      return (
        typeof r.field === 'string' &&
        r.field.length > 0 &&
        r.field.length <= 100 &&
        (r.boundary === 'admission' || r.boundary === 'persistence') &&
        (r.unit === 'characters' || r.unit === 'items') &&
        Number.isSafeInteger(r.original) &&
        Number.isSafeInteger(r.retained) &&
        r.retained >= 0 &&
        r.original > r.retained
      )
    })
  )
    return undefined
  return value.map(({ field, boundary, unit, original, retained }) => ({
    field,
    boundary,
    unit,
    original,
    retained,
  }))
}

/** Keep the first loss across reloads. Re-reading the retained prefix must not
 * erase the record of its missing suffix. Counts use JS string code units. */
export function textRetention(
  boundary: TextTruncation['boundary'],
  prior?: unknown,
  priorVersion?: unknown,
) {
  const parsed = parseTextTruncations(prior)
  const losses = parsed ?? []
  const validPrior =
    prior === undefined || (Array.isArray(prior) && prior.length === 0) || parsed !== undefined
  const record = (
    field: string,
    unit: TextTruncation['unit'],
    original: number,
    retained: number,
  ) => {
    if (original <= retained) return
    const existing = losses.find((r) => r.field === field && r.unit === unit)
    if (existing) {
      existing.original = Math.max(existing.original, original)
      existing.retained = Math.min(existing.retained, retained)
    } else losses.push({ field, boundary, unit, original, retained })
  }
  const text = (field: string, raw: unknown, max = RESEARCH_TEXT_CHARS) => {
    const value = typeof raw === 'string' ? raw.trim() : ''
    record(field, 'characters', value.length, Math.min(value.length, max))
    return value.slice(0, max)
  }
  return {
    text,
    list(field: string, raw: unknown, cap = 8, max = RESEARCH_ITEM_CHARS) {
      if (!Array.isArray(raw)) return []
      const values = raw.filter((v): v is string => typeof v === 'string' && !!v.trim())
      record(field, 'items', values.length, Math.min(values.length, cap))
      return values.slice(0, cap).map((v, i) => text(`${field}[${i}]`, v, max))
    },
    fields: () => ({
      ...(boundary === 'admission' || (priorVersion === 1 && validPrior)
        ? { textRetentionVersion: 1 as const }
        : {}),
      ...(losses.length ? { textTruncations: losses } : {}),
    }),
  }
}

export function textTruncationNotes(value: { textTruncations?: TextTruncation[] }) {
  return (value.textTruncations ?? []).map(
    (r) =>
      `Text shortened during ${r.boundary}: ${r.field} retains ${r.retained} of ${r.original} ${r.unit}. Missing content cannot support a complete argument.`,
  )
}

export function nodeTextTruncationNotes(value: {
  textTruncations?: TextTruncation[]
  elaboration?: { textTruncations?: TextTruncation[] }
}) {
  return [
    ...textTruncationNotes(value),
    ...(value.elaboration
      ? textTruncationNotes(value.elaboration).map((note) => `Elaboration: ${note}`)
      : []),
  ]
}

/** Absence is unknown for historical output, not a measured zero losses. */
export function nodeTextRetentionStatus(value: {
  textRetentionVersion?: 1
  textTruncations?: TextTruncation[]
  elaboration?: { textRetentionVersion?: 1; textTruncations?: TextTruncation[] }
}) {
  if (nodeTextTruncationNotes(value).length) return 'truncated' as const
  if (
    value.textRetentionVersion !== 1 ||
    (value.elaboration && value.elaboration.textRetentionVersion !== 1)
  )
    return 'unrecorded' as const
  return 'retained' as const
}

export function nodeTextRetentionNotes(
  value: Parameters<typeof nodeTextRetentionStatus>[0] & { model?: unknown },
) {
  const notes = nodeTextTruncationNotes(value)
  if (value.model && nodeTextRetentionStatus(value) === 'unrecorded')
    notes.push(
      'Historical field retention is unrecorded. A completion or terminal flag does not establish that the full argument was retained.',
    )
  return notes
}

/**
 * A sentence ends at its stop, any closing quote or bracket after it, and then
 * whitespace before something that is not a lower-case letter — so "e.g. the"
 * and "3.5 mg" are not ends, and a sentence that stops exactly at the cap still is.
 */
const SENTENCE_END = /[.!?…]+["'”’)\]]*(?=\s+[^\s\p{Ll}])/gu

/**
 * Text held to `max` characters, cut where a sentence ends rather than wherever
 * the count ran out.
 *
 * A slice at a character count cut five of eleven synthesis rationales a study
 * read mid-word ("…the membrane pot"), and half a word is not a shorter reason, it
 * is a reason a reader has to repair. So the cut goes back to the last sentence
 * that fits. Where that would keep less than half of what fits — one short
 * sentence before a long one — it goes back to the last whole word instead and
 * says so with an ellipsis, because dropping most of an argument to end on a full
 * stop trades a readable line for a missing one. Only a single word longer than
 * the cap is cut inside itself, and nothing else can be.
 */
export function clipToSentence(raw: string, max: number): string {
  const text = raw.trim()
  if (text.length <= max) return text
  let end = 0
  for (const m of text.matchAll(SENTENCE_END)) {
    const at = m.index + m[0].length
    if (at > max) break
    end = at
  }
  if (end >= max / 2) return text.slice(0, end)
  // The last whole word that leaves room for the ellipsis.
  const room = text.slice(0, max - 1)
  const space = /\s/.test(text[max - 1] ?? '') ? max - 1 : room.search(/\s\S*$/)
  return space > 0
    ? `${text.slice(0, space).replace(/[\s,;:—–-]+$/, '')}…`
    : `${text.slice(0, max - 1)}…`
}
